/**
 * Converts every numeric item inside array-valued properties of `obj` to strings.
 * Mutates and returns `obj`.
 * @param {Record<string, any[]>} obj
 */
export const convertNumbersToStrings = (obj) => {
	for (const key in obj) {
		if (Array.isArray(obj[key])) {
			obj[key] = obj[key].map((item) => (typeof item === 'number' ? String(item) : item))
		}
	}
	return obj
}

/**
 * Deep-equality check for plain objects and primitives.
 * @param {any} obj1
 * @param {any} obj2
 * @returns {boolean}
 */
export const deepEqual = (obj1, obj2) => {
	if (obj1 === obj2) return true

	if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) {
		return false
	}

	const keys1 = Object.keys(obj1)
	const keys2 = Object.keys(obj2)

	if (keys1.length !== keys2.length) return false

	for (const key of keys1) {
		if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
			return false
		}
	}

	return true
}

/**
 * Extracts the numeric values that are marked as matched (`isMatch !== false`)
 * from both the `first` and `last` number blocks.
 * @param {{first: Array, last: Array|null}} value
 * @returns {number[]}
 */
export const getNumbersMatched = (value) => {
	const numbers = { ...value }
	const list = []
	numbers.first.forEach((num) => {
		if (num.isMatch === false) return
		list.push(num.number)
	})

	if (numbers.last === null) return list
	numbers.last.forEach((num) => {
		if (num.isMatch === false) return
		list.push(num.number)
	})
	return list
}

/**
 * Concatenates the `first` and `last` number arrays (including `null` slots).
 * @param {{first: any[], last: any[]|null}} param0
 * @returns {any[]}
 */
export const getNumbers = ({ first, last }) => {
	const list = [...first]
	if (last === null) return list
	list.push(...last)
	return list
}

/**
 * Returns all non-null values from both `first` and `last` blocks combined.
 * @param {{first: any[]|null, last: any[]|null}} param0
 * @returns {any[]}
 */
export const getOnlyNumbers = ({ first, last }) => {
	return [...(first || []), ...(last || [])].filter((n) => n != null)
}

/**
 * Returns `true` if at least one non-null number exists in either block.
 * @param {{first: any[]|null, last: any[]|null}} param0
 * @returns {boolean}
 */
export const hasNumber = ({ first, last }) => {
	const combined = [...(first || []), ...(last || [])]
	return combined.some((n) => n !== null)
}

/**
 * Rounds `num` to three decimal places.
 * @param {number} num
 * @returns {number}
 */
export const toThreeDecimals = (num) => {
	return parseFloat(num.toFixed(3))
}

/**
 * Removes null values from both `first` and `last` blocks, returning new arrays.
 * `last` becomes `null` if it was empty or absent.
 * @param {{first: any[], last: any[]|null}} param0
 * @returns {{first: any[], last: any[]|null}}
 */
export const filterOnlyNumbersByGroup = ({ first, last }) => {
	return {
		first: first.filter((n) => n != null),
		last: last && last.length > 0 ? last.filter((n) => n != null) : null,
	}
}
